import React, { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { H2, P, Callout, Blocks, CheckoutButtons, ConsultLink, ConsultTextLink, PricingTable, Stories, CheckList, GuaranteeBadge, GOLD } from "../components/letter/LetterBlocks";
import {
  HERO, OPENING, TIMELINE, INCLUDED, MEDICAL_NOTE, INVESTMENT_INTRO, AFTER_PRICING, GUARANTEE, FIT, FAQ, DECISION, STEPS, SIGNOFF, PS, PPS, LEGAL, CTA_FULL_FINAL_SUB,
} from "../lib/salesLetterCopy";

const Eyebrow = ({ children }) => (
  <p className="text-[11px] md:text-xs font-bold tracking-[0.22em] uppercase" style={{ color: GOLD }}>{children}</p>
);

const Hero = () => (
  <section className="relative overflow-hidden border-b border-white/10 bg-[#0d0d0d]" data-testid="letter-hero">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,53,0.14),transparent_55%)]" />
    <div className="relative w-[90%] max-w-[760px] mx-auto pt-16 pb-14 md:pt-24 md:pb-20">
      <Eyebrow>Sassy Full Stack Fitness</Eyebrow>
      <p className="mt-4 text-sm md:text-base font-semibold uppercase tracking-wide leading-snug" style={{ color: GOLD }} data-testid="letter-kicker">{HERO.kicker}</p>
      <h1 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.98] text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="letter-h1">
        {HERO.h1a}
        <br />
        <span className="text-gold-gradient">{HERO.h1b}</span>
      </h1>
      <p className="mt-7 text-lg md:text-xl text-white/80 leading-relaxed">{HERO.deck}</p>
    </div>
  </section>
);

const Timeline = () => (
  <ol className="mt-8 relative border-l border-white/15 ml-3 space-y-10" data-testid="letter-timeline">
    {TIMELINE.map((t) => (
      <li key={t.when} className="pl-8 relative">
        <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full" style={{ background: GOLD }} />
        <Eyebrow>{t.when}</Eyebrow>
        <h3 className="mt-1 font-display font-black uppercase tracking-tight text-xl md:text-2xl text-white">{t.title}</h3>
        <div className="mt-3 space-y-3">{t.body.map((p) => <P key={p.slice(0, 24)}>{p}</P>)}</div>
      </li>
    ))}
  </ol>
);

const Included = () => (
  <div className="mt-8 space-y-8" data-testid="letter-included">
    {INCLUDED.map((g) => (
      <div key={g.group}>
        <Eyebrow>{g.group}</Eyebrow>
        <div className="mt-3"><CheckList items={g.items} yes /></div>
      </div>
    ))}
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6 space-y-3">
      {MEDICAL_NOTE.map((p) => <p key={p.slice(0, 24)} className="text-[15px] md:text-base text-white/75 leading-relaxed">{p}</p>)}
    </div>
  </div>
);

const Guarantee = () => (
  <section className="mt-16 rounded-3xl border p-6 md:p-10" style={{ borderColor: "rgba(212,175,53,.45)", background: "rgba(212,175,53,.06)" }} data-testid="letter-guarantee">
    <h2 className="font-display font-black uppercase tracking-tight leading-[1.05] text-[26px] md:text-[34px] text-white">{GUARANTEE.title}</h2>
    <P className="mt-4">{GUARANTEE.intro}</P>
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
      {GUARANTEE.promises.map((pr) => (
        <div key={pr.eyebrow} className="rounded-2xl bg-[#0d0d0d] border border-white/10 p-5 md:p-6">
          <Eyebrow>{pr.eyebrow}</Eyebrow>
          <h3 className="mt-1 font-display font-black uppercase tracking-tight text-lg md:text-xl text-white">{pr.title}</h3>
          <div className="mt-3 space-y-3">{pr.body.map((p) => <p key={p.slice(0, 24)} className="text-[15px] md:text-base text-white/75 leading-relaxed">{p}</p>)}</div>
        </div>
      ))}
    </div>
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-display font-black uppercase tracking-tight text-lg text-white">{GUARANTEE.needFromYou.title}</h3>
        <p className="mt-2 text-[15px] md:text-base text-white/75 leading-relaxed">{GUARANTEE.needFromYou.body}</p>
      </div>
      <div>
        <h3 className="font-display font-black uppercase tracking-tight text-lg text-white">{GUARANTEE.fromMe.title}</h3>
        <div className="mt-2 space-y-2">{GUARANTEE.fromMe.body.map((p) => <p key={p.slice(0, 20)} className="text-[15px] md:text-base text-white/75 leading-relaxed">{p}</p>)}</div>
      </div>
    </div>
  </section>
);

const Fit = () => (
  <>
    <H2>Is this for you?</H2>
    <div className="space-y-5">{FIT.intro.map((p) => <P key={p.slice(0, 24)}>{p}</P>)}</div>
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="letter-fit">
      <div className="rounded-2xl border border-[#D4AF35]/40 bg-[#D4AF35]/5 p-5 md:p-6">
        <Eyebrow>Full Stack Coaching is for you if:</Eyebrow>
        <div className="mt-4"><CheckList items={FIT.yes} yes /></div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
        <p className="text-[11px] md:text-xs font-bold tracking-[0.22em] uppercase text-white/55">It's not for you if:</p>
        <div className="mt-4"><CheckList items={FIT.no} /></div>
      </div>
    </div>
    <P className="mt-6 font-semibold text-white">{FIT.outro}</P>
  </>
);

const Faq = () => (
  <>
    <H2>Questions, answered</H2>
    <div className="divide-y divide-white/10" data-testid="letter-faq">
      {FAQ.map(([q, a, kind]) => (
        <div key={q} className="py-5">
          <h3 className="font-bold text-white text-[17px] md:text-lg">{q}</h3>
          {a.split("\n\n").map((para) => <p key={para.slice(0, 24)} className="mt-2 text-[16px] md:text-[17px] text-white/75 leading-relaxed">{para}</p>)}
          {kind === "consult" && <div className="mt-3"><ConsultLink testid="letter-consult-faq">Book a free 30-minute consultation</ConsultLink></div>}
        </div>
      ))}
    </div>
  </>
);

const Closing = () => (
  <>
    <H2>Your decision</H2>
    <div className="space-y-5">{DECISION.map((p) => <P key={p.slice(0, 24)}>{p}</P>)}</div>
    <CheckoutButtons position="final" fullSub={CTA_FULL_FINAL_SUB} />
    <GuaranteeBadge />
    <h3 className="mt-14 font-display font-black uppercase tracking-tight text-xl md:text-2xl text-white">What happens after you click</h3>
    <ol className="mt-5 space-y-4" data-testid="letter-steps">
      {STEPS.map(([title, detail], i) => (
        <li key={title} className="flex gap-4">
          <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm text-[#0d0d0d]" style={{ background: GOLD }}>{i + 1}</span>
          <p className="text-[16px] md:text-[17px] text-white/80 leading-relaxed"><span className="font-bold text-white">{title}</span> {detail}</p>
        </li>
      ))}
    </ol>
    <Callout>{SIGNOFF.line}</Callout>
    <div className="mt-10" data-testid="letter-signoff">
      <P>{SIGNOFF.farewell}</P>
      <p className="mt-6 font-display font-black text-2xl text-white">{SIGNOFF.name}</p>
      <p className="text-sm font-semibold tracking-wide" style={{ color: GOLD }}>{SIGNOFF.creds}</p>
      <p className="text-sm text-white/60">{SIGNOFF.role}</p>
    </div>
    <div className="mt-12 space-y-5 border-t border-white/10 pt-10">
      <P><span className="font-bold text-white">P.S.</span> {PS}</P>
      <P><span className="font-bold text-white">P.P.S.</span> {PPS.split("Book a free 30-minute consultation with me.")[0]}<ConsultTextLink testid="letter-consult-pps">Book a free 30-minute consultation with me.</ConsultTextLink>{PPS.split("Book a free 30-minute consultation with me.")[1]}</P>
    </div>
    <p className="mt-12 text-xs text-white/45 leading-relaxed">{LEGAL}</p>
  </>
);

export default function SalesLetterPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "You're Not Doing It Wrong. You're Doing Half of It. | Sassy Full Stack Fitness";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  return (
    <div className="bg-background min-h-screen flex flex-col" data-testid="sales-letter-page">
      <Header minimal />
      <main className="flex-1 pt-16">
        <Hero />
        <article className="w-[90%] max-w-[720px] mx-auto pt-12 pb-24">
          <Blocks blocks={OPENING} />
          <Timeline />
          <H2>Everything included</H2>
          <Included />
          <H2>Women who got it back <span className="text-white/50">(ages 39 to 73)</span></H2>
          <P>Don't take my word for it. These women started exactly where you are.</P>
          <Stories />
          <H2 id="investment">Your investment</H2>
          <div className="space-y-5">{INVESTMENT_INTRO.map((p) => <P key={p.slice(0, 24)}>{p}</P>)}</div>
          <PricingTable />
          <div className="mt-8 space-y-5">{AFTER_PRICING.map((p) => <P key={p.slice(0, 24)}>{p}</P>)}</div>
          <Guarantee />
          <Fit />
          <Faq />
          <Closing />
        </article>
      </main>
      <Footer />
    </div>
  );
}
